import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiBarChart2, FiCheck, FiClock, FiPlus, FiSettings, FiTrash2, FiX } from 'react-icons/fi';
import { useSubmissionCenterStats } from '../../../context/SubmissionCenterStatsContext';
import { useToast } from '../../../context/ToastContext';
import QuotePolicyDetailPanel from './QuotePolicyDetailPanel';
import OpportunityNotesPanel from './OpportunityNotesPanel';
import { fetchLinesOfBusinessDetailed, type LineOfBusinessOption } from './submissionApi';
import AqSelect from './AqSelect';
import '../../../styles/CommandCenterSidebar.css';
import '../../../styles/SubmissionCenter.css';
import '../../../styles/AddQuotePage.css';
import '../../commissionscan/CommissionScan.css';

const ACCOUNT_AMP_BASE = process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com';

/** Neutral-metric color rotation; a KPI carrying a tone (crit/warn) overrides it. */
const PALETTE = ['purple', 'cyan', 'green', 'blue'];

type SidebarTab = 'dashboard' | 'config';

const SubmissionCenterSidebarContent: React.FC = () => {
  const { kpis, selected, setSelected } = useSubmissionCenterStats();
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('dashboard');

  // Same real lines-of-business list AddOpportunityPage's Step 2 already fetches
  // (AMPNowcertsController's GetLinesOfBusiness) — fetched once, lazily, the first
  // time the Configuration tab is opened rather than unconditionally on mount.
  // Kept as full {id, name} records (not just names) because saving steps needs
  // the selected line of business's id, not just its display name.
  const [lobRecords, setLobRecords] = useState<LineOfBusinessOption[]>([]);
  const [lobLoading, setLobLoading] = useState(false);
  const [selectedLob, setSelectedLob] = useState('');
  const [lobQuery, setLobQuery] = useState('');
  // True once the very first (unfiltered) load has resolved — kept separate from
  // lobOptions.length so a search that legitimately matches nothing doesn't
  // disable/relabel the field as if the agency had no lines of business at all.
  const [lobEverLoaded, setLobEverLoaded] = useState(false);
  const fetchingLobRef = useRef(false);
  const lobSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lobQueryTouchedRef = useRef(false);
  const lobOptions = useMemo(() => lobRecords.map((r) => r.name), [lobRecords]);
  const selectedLobRecord = useMemo(
    () => lobRecords.find((r) => r.name === selectedLob),
    [lobRecords, selectedLob],
  );

  // Steps attached to the selected line of business. `id` is set for a step that
  // already exists on the server (from GET /api/OpportunitiesSubmission/get) and
  // left undefined for one added locally via "Add steps" that hasn't been saved yet.
  const [customFields, setCustomFields] = useState<{ id?: number; title: string; description: string }[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const fetchingStepsRef = useRef(false);
  const addCustomField = () => setCustomFields((prev) => [...prev, { title: '', description: '' }]);
  const updateCustomField = (idx: number, key: 'title' | 'description', value: string) =>
    setCustomFields((prev) => prev.map((f, i) => (i === idx ? { ...f, [key]: value } : f)));
  const removeCustomField = (idx: number) =>
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));

  // Loads whatever steps already exist for a line of business — GET
  // /api/OpportunitiesSubmission/get?lineOfBusinessId=... — so the panel shows
  // what's already saved instead of always starting from an empty list. Also
  // re-run after a successful save, so a step that was just added (no id yet,
  // rendered as the plain editable "new step" box) picks up its real server id
  // and switches to the compact saved-step display instead of staying in edit mode.
  const fetchSteps = useCallback((lobId: string) => {
    if (!lobId || fetchingStepsRef.current) return;
    fetchingStepsRef.current = true;
    setStepsLoading(true);
    let cancelled = false;
    axios
      .get(`${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/get`, {
        params: { lineOfBusinessId: lobId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
          NCToken: localStorage.getItem('amsToken') || '',
        },
      })
      .then((res) => {
        if (cancelled) return;
        const steps = Array.isArray(res.data?.steps) ? res.data.steps : [];
        setCustomFields(
          steps.map((s: { id?: number; title?: string; description?: string }) => ({
            id: s.id,
            title: s.title || '',
            description: s.description || '',
          })),
        );
      })
      .catch((err) => console.log('OpportunitiesSubmission/get error:', err))
      .finally(() => {
        fetchingStepsRef.current = false;
        if (!cancelled) setStepsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setCustomFields([]);
    setConfirmDeleteIdx(null);
    const lobId = selectedLobRecord?.id;
    if (!lobId) return;
    return fetchSteps(lobId);
  }, [selectedLobRecord?.id, fetchSteps]);

  // Saves this line of business's steps — POST /api/OpportunitiesSubmission/insert,
  // backed by SubmissionCenterTrackingHistoryRequest { LineOfBusinessId,
  // LineOfBusinessName, Steps: [{Title, Description}] }. Success/error surface the
  // same way Command Center's own save actions do — via the shared toast stack
  // (useToast), not a bespoke inline message.
  const { showSuccess, showError } = useToast();
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const handleSaveSteps = async () => {
    if (!selectedLobRecord || !customFields.length) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      const res = await axios.post(
        `${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/insert`,
        {
          LineOfBusinessId: selectedLobRecord.id,
          LineOfBusinessName: selectedLobRecord.name,
          Steps: customFields.map((f) => ({ Title: f.title, Description: f.description })),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
      );
      showSuccess(res.data);
      fetchSteps(selectedLobRecord.id);
    } catch (err: any) {
      console.log('OpportunitiesSubmission/insert error:', err);
      setSaveFailed(true);
      showError(err?.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  // Deletes one step — DELETE /api/OpportunitiesSubmission/delete/{id}, id being the
  // step's own id from GET .../get. A step that was only just added locally (no id
  // yet, never saved) has nothing to delete on the server — just drop it from state.
  // Either way, the trash icon opens an inline "Delete this step?" confirm first
  // rather than removing immediately.
  const [deletingStepIds, setDeletingStepIds] = useState<Set<number>>(new Set());
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const handleRemoveStep = async (idx: number) => {
    setConfirmDeleteIdx(null);
    const step = customFields[idx];
    const id = step?.id;
    if (id == null) {
      removeCustomField(idx);
      return;
    }
    if (deletingStepIds.has(id)) return;
    setDeletingStepIds((prev) => new Set(prev).add(id));
    try {
      const res = await axios.delete(`${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
      });
      showSuccess(res.data);
      removeCustomField(idx);
    } catch (err: any) {
      console.log('OpportunitiesSubmission/delete error:', err);
      showError(err?.response?.data || err);
    } finally {
      setDeletingStepIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };
  useEffect(() => { setSaveFailed(false); }, [selectedLob]);

  // Unfiltered load — fires once, lazily, the first time the Configuration
  // tab is opened (same as before the search box was added).
  useEffect(() => {
    if (sidebarTab !== 'config' || fetchingLobRef.current || lobRecords.length) return;
    fetchingLobRef.current = true;
    setLobLoading(true);
    let cancelled = false;
    fetchLinesOfBusinessDetailed()
      .then((list) => { if (!cancelled) setLobRecords(list); })
      .catch(() => {})
      .finally(() => {
        fetchingLobRef.current = false;
        if (!cancelled) { setLobLoading(false); setLobEverLoaded(true); }
      });
    return () => { cancelled = true; };
  }, [sidebarTab, lobRecords.length]);

  // Search-as-you-type — re-queries NowCerts itself (odata $filter=name eq
  // '<query>') via GetLinesOfBusiness's `name` param instead of filtering the
  // list already in state, so results match agency-side data even beyond
  // whatever page the plain load above pulled. Only kicks in once the user
  // has actually typed in the search box (lobQueryTouchedRef), so it doesn't
  // duplicate the unfiltered load that already runs on first tab-open.
  useEffect(() => {
    if (sidebarTab !== 'config' || !lobQueryTouchedRef.current) return;
    if (lobSearchDebounceRef.current) clearTimeout(lobSearchDebounceRef.current);
    let cancelled = false;
    lobSearchDebounceRef.current = setTimeout(() => {
      setLobLoading(true);
      fetchLinesOfBusinessDetailed(lobQuery)
        .then((list) => { if (!cancelled) setLobRecords(list); })
        .catch(() => { if (!cancelled) setLobRecords([]); })
        .finally(() => { if (!cancelled) setLobLoading(false); });
    }, 400);
    return () => {
      cancelled = true;
      if (lobSearchDebounceRef.current) clearTimeout(lobSearchDebounceRef.current);
    };
  }, [sidebarTab, lobQuery]);

  if (selected) {
    return (
      <div className="cc-sb-dashboard">
        {selected.kind === 'notes'
          ? <OpportunityNotesPanel opportunityId={selected.opportunityId} insuredDatabaseId={selected.insuredDatabaseId} onClose={() => setSelected(null)} />
          : <QuotePolicyDetailPanel policyId={selected.policyId} opportunityId={selected.opportunityId} />}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Tab bar — same pill toggle as Command Center's sidebar (.cc-sb-tab*,
          shared via CommandCenterSidebar.css, also reused by DialerSidebarContent). ── */}
      <div className="cc-sb-tabs">
        <div className="cc-sb-tab-track">
          <button
            className={`cc-sb-tab${sidebarTab === 'dashboard' ? ' cc-sb-tab--active cc-sb-tab--active-dashboard' : ''}`}
            onClick={() => setSidebarTab('dashboard')}
          >
            <FiBarChart2 size={11} />
            Analytics
          </button>
          <button
            className={`cc-sb-tab${sidebarTab === 'config' ? ' cc-sb-tab--active cc-sb-tab--active-config' : ''}`}
            onClick={() => setSidebarTab('config')}
          >
            <FiSettings size={11} />
            Configuration
          </button>
        </div>
      </div>

      {sidebarTab === 'dashboard' ? (
        <div className="cc-sb-dashboard">
          <div className="cc-sb-stats">
            {kpis.map((k, i) => {
              const color = k.tone === 'crit' ? 'red' : k.tone === 'warn' ? 'amber' : PALETTE[i % PALETTE.length];
              return (
                <div key={k.label} className={`cc-sb-stat cc-sb-stat--${color}`}>
                  <div className="cc-sb-stat__body">
                    <span className="cc-sb-stat__label">{k.label}</span>
                    <span className="cc-sb-stat__context">{k.foot}</span>
                  </div>
                  <span className={`cc-sb-stat__num cc-sb-stat__num--${color}`}>{k.value}</span>
                </div>
              );
            })}
          </div>

          {/* Teaser mini-dashboard — no real opportunity-analytics data source
              wired up yet. Every chart here is decorative/illustrative (fixed
              mock values, aria-hidden) — the "Coming soon" badge overlaid on
              top is the only actual content, so it can't be mistaken for a
              live view. */}
          <div className="cc-sb-viz-dash">
            <span className="cc-sb-viz__badge cc-sb-viz-dash__overlay-badge"><FiClock size={9} /> Coming soon</span>

            <div className="cc-sb-viz-dash__body" aria-hidden="true">
              <div className="cc-sb-viz-grid">
                <div className="cc-sb-viz-card">
                  <div className="cc-sb-viz-card__chart">
                    <div className="cc-sb-viz__bars cc-sb-viz__bars--sm">
                      {[85, 62, 40, 22].map((h, i) => (
                        <div key={i} className="cc-sb-viz__bar" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>
                  <span className="cc-sb-viz-card__label">Stage funnel</span>
                </div>

                <div className="cc-sb-viz-card">
                  <div className="cc-sb-viz-card__chart">
                    <svg viewBox="0 0 36 36" className="cc-sb-viz__donut">
                      <circle cx="18" cy="18" r="15.5" className="cc-sb-viz__donut-track" />
                      <circle
                        cx="18" cy="18" r="15.5" className="cc-sb-viz__donut-value"
                        strokeDasharray="68 32" strokeDashoffset="25"
                      />
                    </svg>
                  </div>
                  <span className="cc-sb-viz-card__label">Win rate</span>
                </div>
              </div>

              <div className="cc-sb-viz-card cc-sb-viz-card--wide">
                <div className="cc-sb-viz-card__chart">
                  <svg viewBox="0 0 120 34" preserveAspectRatio="none" className="cc-sb-viz__line">
                    <polyline points="0,26 15,22 30,24 45,14 60,17 75,8 90,12 105,4 120,7" />
                  </svg>
                </div>
                <span className="cc-sb-viz-card__label">Opportunities created over time</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="cc-sb-dashboard">
          <div className="aq-field">
            <label className="aq-label">Line of business</label>
            <AqSelect
              value={selectedLob}
              onChange={setSelectedLob}
              options={lobOptions}
              placeholder={
                lobLoading && !lobEverLoaded
                  ? 'Loading…'
                  : lobOptions.length || lobQuery.trim()
                    ? 'Select a line of business'
                    : 'No lines of business found'
              }
              disabled={!lobEverLoaded}
              searchable
              portal
              onSearchChange={(q) => { lobQueryTouchedRef.current = true; setLobQuery(q); }}
            />
          </div>

          {selectedLob && (
            <div className="aq-field" style={{ marginTop: 14 }}>
              {stepsLoading ? (
                [0, 1, 2].map((i) => (
                  <div className={`cc-sb-stat cc-sb-stat--${PALETTE[i % PALETTE.length]}`} key={i} style={{ marginBottom: 10 }}>
                    <div className="cc-sb-stat__body" style={{ flex: 1, minWidth: 0 }}>
                      <div className="sc__skel-bone" style={{ width: '45%', height: 9 }} />
                      <div className="sc__skel-bone" style={{ width: '80%', height: 12 }} />
                    </div>
                  </div>
                ))
              ) : customFields.map((field, idx) => {
                // A step already saved on the server (has a real id from GET .../get)
                // shows in the compact .cc-sb-stat display style. A step just added
                // locally via "Add steps" — not saved yet — shows as a plain editable
                // form field instead, like before, so it's obvious it's still being filled in.
                if (field.id == null) {
                  return (
                    <div
                      key={`new-${idx}`}
                      style={{
                        display: 'flex', flexDirection: 'column', gap: 6,
                        padding: 10, marginBottom: 10,
                        border: '1px solid var(--border-glass)', borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label className="aq-label">Step {idx + 1}</label>
                        {confirmDeleteIdx === idx ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="sc-panel__note" style={{ fontSize: 11 }}>Delete?</span>
                            <button type="button" className="aq-doc-remove" aria-label="Confirm delete" onClick={() => handleRemoveStep(idx)}>
                              <FiCheck size={13} />
                            </button>
                            <button type="button" className="aq-doc-remove" aria-label="Cancel delete" onClick={() => setConfirmDeleteIdx(null)}>
                              <FiX size={13} />
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="aq-doc-remove"
                            aria-label={`Remove step ${idx + 1}`}
                            onClick={() => setConfirmDeleteIdx(idx)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        )}
                      </div>
                      <input
                        className="glass-input"
                        style={{ height: 30, fontSize: 12, padding: '0 10px', boxSizing: 'border-box' }}
                        value={field.title}
                        onChange={(e) => updateCustomField(idx, 'title', e.target.value)}
                        placeholder="Title"
                      />
                      <textarea
                        className="glass-input aq-note"
                        rows={2}
                        style={{ fontSize: 12, padding: '6px 10px' }}
                        value={field.description}
                        onChange={(e) => updateCustomField(idx, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </div>
                  );
                }

                // Reset only box-model properties — deliberately NOT color/font, so the
                // input picks up .cc-sb-stat__label / .cc-sb-stat__context's own explicit,
                // theme-aware colors instead of an ambient `inherit` chain (which is why
                // the description used to render too faint to read).
                const bareInput: React.CSSProperties = {
                  border: 'none', background: 'transparent', outline: 'none', width: '100%', padding: 0,
                };
                const color = PALETTE[idx % PALETTE.length];
                return (
                  <div
                    className={`cc-sb-stat cc-sb-stat--${color}`}
                    key={field.id}
                    style={{ marginBottom: 10 }}
                  >
                    <div className="cc-sb-stat__body" style={{ flex: 1, minWidth: 0 }}>
                      <input
                        className="cc-sb-stat__label"
                        style={bareInput}
                        value={field.title}
                        onChange={(e) => updateCustomField(idx, 'title', e.target.value)}
                        placeholder="TITLE"
                      />
                      <input
                        className="cc-sb-stat__context"
                        style={bareInput}
                        value={field.description}
                        onChange={(e) => updateCustomField(idx, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </div>
                    {confirmDeleteIdx === idx ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span className="sc-panel__note" style={{ fontSize: 11 }}>Delete?</span>
                        <button
                          type="button"
                          className="aq-doc-remove"
                          aria-label="Confirm delete"
                          disabled={deletingStepIds.has(field.id)}
                          onClick={() => handleRemoveStep(idx)}
                        >
                          <FiCheck size={12} />
                        </button>
                        <button type="button" className="aq-doc-remove" aria-label="Cancel delete" onClick={() => setConfirmDeleteIdx(null)}>
                          <FiX size={12} />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="aq-doc-remove"
                        aria-label={`Remove step ${idx + 1}`}
                        disabled={deletingStepIds.has(field.id)}
                        onClick={() => setConfirmDeleteIdx(idx)}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  className="cs-browse-btn cs-browse-btn--sm"
                  onClick={addCustomField}
                >
                  <FiPlus size={13} /> Add steps
                </button>
                <button
                  type="button"
                  className="cs-browse-btn cs-browse-btn--sm"
                  disabled={!customFields.length || saving}
                  onClick={handleSaveSteps}
                >
                  {saving ? 'Saving…' : saveFailed ? 'Retry' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmissionCenterSidebarContent;
